// src/pages/orders/model/adapters.ts

import {
  DriverOnShift,
  Job,
  JobStatus,
  JobType,
  Organization,
  PickupLocation,
  User,
  ContainerType,
  ConnectProto,
} from "@racoon/api";

// Union type to accept both REST API Job and gRPC Job
type JobInput = Job | ConnectProto.Job;
import { getTypeMap } from "~/shared/lib/translationMaps";
import { fromTimestamp } from "@racoon/lib";
import type { AttachedInstructionPhoto } from "~/shared/lib/instruction-photos";

// Helper to convert protobuf Timestamp or any date value to ISO string
function toISOString(value: any): string {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string") return value;
  // Handle protobuf Timestamp with seconds and nanos
  if (value.seconds !== undefined) {
    const date = fromTimestamp(value);
    return date ? date.toISOString() : "";
  }
  // Fallback: try to create a date
  try {
    return new Date(value).toISOString();
  } catch {
    return "";
  }
}

const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const camelToSnakeKey = (key: string) =>
  key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
const isProtoTimestamp = (value: any) =>
  value && typeof value === "object" && !Array.isArray(value) && value.seconds !== undefined;

// gRPC decodes enum fields as their numeric wire value, not the string name (e.g. job.type === 4).
// The REST API, translations (Namespace.Enum), and most comparisons expect the string name
// (e.g. "JOB_TYPE_REMOVE_BUNKER"), so resolve known enum fields to their name before use.
const resolveEnum = (schema: any, value: any) =>
  typeof value === "number"
    ? (schema.values.find((v: any) => v.number === value)?.name ?? value)
    : value;

// RTK Query freezes cached response data in dev mode, so this must return new objects
// rather than mutate jobAny (or its nested objects) in place.
function resolveUserEnums(user: any): any {
  if (!user) return user;
  return {
    ...user,
    role: resolveEnum(ConnectProto.UserRoleSchema, user.role),
    status: resolveEnum(ConnectProto.UserStatusSchema, user.status),
  };
}

/** Returns a new job object with every known numeric enum field resolved to its string name. */
function resolveJobEnums(jobAny: any): any {
  if (!jobAny) return jobAny;

  const pickupLocation = jobAny.pickupLocation
    ? {
        ...jobAny.pickupLocation,
        type: resolveEnum(ConnectProto.PickupLocationTypeSchema, jobAny.pickupLocation.type),
        status: resolveEnum(ConnectProto.LocationStatusSchema, jobAny.pickupLocation.status),
        priority: resolveEnum(
          ConnectProto.PickupLocationPrioritySchema,
          jobAny.pickupLocation.priority,
        ),
      }
    : jobAny.pickupLocation;

  const serviceReceiver = jobAny.serviceReceiver
    ? {
        ...jobAny.serviceReceiver,
        type: resolveEnum(ConnectProto.OrganizationTypeSchema, jobAny.serviceReceiver.type),
        status: resolveEnum(ConnectProto.OrganizationStatusSchema, jobAny.serviceReceiver.status),
      }
    : jobAny.serviceReceiver;

  const driverOnShift = jobAny.driverOnShift
    ? {
        ...jobAny.driverOnShift,
        status: resolveEnum(ConnectProto.DriverOnShiftStatusSchema, jobAny.driverOnShift.status),
        driver: resolveUserEnums(jobAny.driverOnShift.driver),
        truck: jobAny.driverOnShift.truck
          ? {
              ...jobAny.driverOnShift.truck,
              type: resolveEnum(ConnectProto.TruckTypeSchema, jobAny.driverOnShift.truck.type),
              status: resolveEnum(
                ConnectProto.TruckStatusSchema,
                jobAny.driverOnShift.truck.status,
              ),
            }
          : jobAny.driverOnShift.truck,
      }
    : jobAny.driverOnShift;

  const fileReports = Array.isArray(jobAny.fileReports)
    ? jobAny.fileReports.map((f: any) => ({
        ...f,
        type: resolveEnum(ConnectProto.FileUploadTypeSchema, f.type),
      }))
    : jobAny.fileReports;

  return {
    ...jobAny,
    type: resolveEnum(ConnectProto.JobTypeSchema, jobAny.type),
    status: resolveEnum(ConnectProto.JobStatusSchema, jobAny.status),
    ...(jobAny.dispatcherReviewStatus !== undefined && {
      dispatcherReviewStatus: resolveEnum(
        ConnectProto.DispatcherReviewStatusSchema,
        jobAny.dispatcherReviewStatus,
      ),
    }),
    ...(jobAny.jobCategory !== undefined && {
      jobCategory: resolveEnum(ConnectProto.JobCategorySchema, jobAny.jobCategory),
    }),
    pickupLocation,
    serviceReceiver,
    author: resolveUserEnums(jobAny.author),
    driverOnShift,
    fileReports,
  };
}

const INSTRUCTION_TYPE = ConnectProto.FileUploadType.JOB_INSTRUCTION;
const INSTRUCTION_TYPE_NAME = resolveEnum(ConnectProto.FileUploadTypeSchema, INSTRUCTION_TYPE);

/**
 * Picks the instruction photos out of a job's file reports, which also carry the
 * driver's service evidence.
 *
 * Matches both enum forms deliberately: a raw gRPC job numbers its enums, while a
 * job that has been through resolveJobEnums carries their names, and the create/edit
 * dialog is handed either one.
 */
export function pickInstructionPhotos(fileReports: unknown): AttachedInstructionPhoto[] {
  if (!Array.isArray(fileReports)) return [];
  return fileReports
    .filter((f: any) => f?.type === INSTRUCTION_TYPE || f?.type === INSTRUCTION_TYPE_NAME)
    .filter((f: any) => f?.id !== undefined && f?.url)
    .map((f: any) => ({ id: BigInt(f.id), url: String(f.url) }));
}

// Deepest path any consumer reads off row.job, e.g. job.driver_on_shift.truck.plate_number
// (job=0, driver_on_shift=1, truck=2). Capping recursion here avoids cloning+mirroring keys
// through large substructures nothing reads, like pickup_location.geometry's coordinate arrays.
const NORMALIZE_JOB_MAX_DEPTH = 3;

/**
 * The gRPC job client (@connectrpc/connect) decodes responses into plain objects with
 * camelCase-only keys (fileReports, driverOnShift, factEndAt as {seconds,nanos}, etc.),
 * while most job UI (photo report modal, whatsapp modal, gps tracks) was written against
 * the REST snake_case Job shape. Rather than patch every read site, normalize once here:
 * mirror every key under its snake_case form and stringify protobuf Timestamps to ISO,
 * so downstream code can keep reading snake_case (and gRPC-aware code can still read camelCase).
 */
function normalizeJobDeep(value: any, depth = 0): any {
  if (value === null || value === undefined) return value;
  if (isProtoTimestamp(value)) return toISOString(value);
  if (value instanceof Date) return value;
  if (depth >= NORMALIZE_JOB_MAX_DEPTH) return value;
  if (Array.isArray(value)) return value.map((item) => normalizeJobDeep(item, depth + 1));
  if (typeof value === "object") {
    const result: any = {};
    for (const key of Object.keys(value)) {
      if (DANGEROUS_KEYS.has(key)) continue;
      const normalized = normalizeJobDeep(value[key], depth + 1);
      result[key] = normalized;
      const snakeKey = camelToSnakeKey(key);
      if (snakeKey !== key && !(snakeKey in value)) {
        result[snakeKey] = normalized;
      }
    }
    return result;
  }
  return value;
}

export type OrderRow = {
  id: number | string;
  number: string; // e.g. Z1391
  typeText: string; // "Replace bunker"
  customer: string; // org name
  objectName: string; // location name
  priorityText: string; // "Medium" (we derive from geo group if you want)
  statusText: string; // "Created" | "Completed"
  geozone: string | number | null;
  route: string | null; // e.g. M1345
  createdAt: string;
  finishedAt?: string | null;
  hasPhotoReport: boolean;
  visible_to_ai?: boolean;
  tariff?: number | string | null;
  status: JobStatus;
  plan_start_at: Date | null;
  pickup_location?: Partial<PickupLocation>;
  service_receiver?: Partial<Organization>;
  service_provider?: Partial<Organization>;
  // The Job proto only carries service_provider as a scalar id (no nested Organization,
  // unlike service_receiver), so it's exposed separately here for update/cancel calls.
  service_provider_id?: string | number;
  driver?: DriverOnShift;
  comment: string;
  job: Job;
  /** Raw ConnectProto job, for consumers (e.g. PhotoReportModal) that now speak gRPC/camelCase directly. */
  rawJob: ConnectProto.Job;
  is_created_by_client?: boolean;
  /** Contact id of the WhatsApp bot dialogue author; set only for bot-created jobs. */
  whatsapp_author_id?: string;
  author_first_name?: string;
  author_last_name?: string;
  author?: User;
  containertype?: ContainerType;
  /** Group header fields for job-map cascading display */
  _isGroupHeader?: boolean;
  /** 1 on an order's header, nested in its pickup location's (see `jobMapRows`). */
  _groupDepth?: number;
  /** On an order's header: whether its jobs are showing (set by the map table). */
  _isExpanded?: boolean;
  /** The row under an order's header that draws its jobs, newest first. */
  _orderJobs?: OrderRow[];
  /** A job's attempt at its order: 1 for the first. */
  _attempt?: number;
  /**
   * What the order map draws for the row (see `jobMapRows`): a pickup location's card, an
   * order's row, the order's jobs, or the gap above the order's card.
   */
  _mapRow?: "location" | "order" | "jobs" | "spacer";
  /** Ties a header to its children for the shared grouped-table helpers. */
  _groupKey?: string;
  _groupLocationName?: string;
  _groupCompletedCount?: number;
  _groupTotalCount?: number;
  _isLastInGroup?: boolean;
};

const statusMap: Record<JobStatus, string> = {
  [JobStatus.JOB_STATUS_CREATED]: "Создан",
  [JobStatus.JOB_STATUS_COMPLETED]: "Выполнен",
  [JobStatus.JOB_STATUS_UNDEFINED]: "",
  [JobStatus.JOB_STATUS_ASSIGNED]: "",
  [JobStatus.JOB_STATUS_ENROUTE]: "",
  [JobStatus.JOB_STATUS_CANCELLED]: "",
  [JobStatus.JOB_STATUS_ON_SERVICE]: "Выполняется",
  [JobStatus.JOB_STATUS_FORCE_COMPLETED]: "Выполнен",
  [JobStatus.JOB_STATUS_NOT_COMPLETED]: "Невывоз",
  [JobStatus.UNRECOGNIZED]: "",
};

export function adaptJobToRow(j: JobInput, t?: any): OrderRow {
  const jobAny = resolveJobEnums(j as any);

  // Convert enum number to string name for gRPC compatibility
  const statusName =
    ConnectProto.JobStatusSchema.values.find((v) => v.number === jobAny.status)?.name ??
    String(jobAny.status);

  // Handle both camelCase (gRPC) and snake_case (REST) field names
  // Replaced || with ?? to avoid overriding valid falsy values (0, false, "")
  const createdAt = jobAny.createdAt ?? jobAny.created_at;
  const factEndAt = jobAny.factEndAt ?? jobAny.fact_end_at;
  const serviceStartedReportUrl =
    jobAny.serviceStartedReportUrl ?? jobAny.service_started_report_url;
  const serviceEndedReportUrl = jobAny.serviceEndedReportUrl ?? jobAny.service_ended_report_url;
  // service_started/ended_report_url are deprecated in favor of file_reports
  const fileReports = jobAny.file_reports ?? jobAny.fileReports ?? [];
  const pickupLocation = jobAny.pickupLocation ?? jobAny.pickup_location;
  const serviceReceiver = jobAny.serviceReceiver ?? jobAny.service_receiver;
  const jobTariff = jobAny.jobTariff ?? jobAny.job_tariff;
  const driverOnShift = jobAny.driverOnShift ?? jobAny.driver_on_shift;
  const planStartAt = jobAny.planStartAt ?? jobAny.plan_start_at;
  const driverComment = jobAny.driverComment ?? jobAny.driver_comment;
  const routeId = jobAny.routeId ?? jobAny.route_id;
  const isCreatedByClient = jobAny.isCreatedByClient ?? jobAny.is_created_by_client;
  const whatsappAuthorId = jobAny.whatsappAuthorId ?? jobAny.whatsapp_author_id;
  const visibleToAi = jobAny.visibleToAi ?? jobAny.visible_to_ai;
  const containerType = jobAny.containerType ?? jobAny.container_type;
  const author = jobAny.author;
  const authorFirstName = jobAny.authorFirstName ?? jobAny.author_first_name;
  const authorLastName = jobAny.authorLastName ?? jobAny.author_last_name;
  const serviceProvider = jobAny.serviceProvider ?? jobAny.service_provider;
  const serviceProviderId = jobAny.serviceProviderId ?? jobAny.service_provider_id;
  // Convert priority enum to string if it's a number
  const priorityValue = pickupLocation?.priority;
  let priorityText = "—";
  if (typeof priorityValue === "number") {
    priorityText =
      ConnectProto.PickupLocationPrioritySchema.values.find((v) => v.number === priorityValue)
        ?.name ?? "—";
  } else if (priorityValue) {
    priorityText = String(priorityValue);
  }
  const typeMap = t
    ? getTypeMap(t)
    : {
        [JobType.JOB_TYPE_ADD_BUNKER]: "Установка бункера",
        [JobType.JOB_TYPE_REPLACE_BUNKER]: "Поменять бункер",
        [JobType.JOB_TYPE_UNDEFINED]: "Не определен",
        [JobType.JOB_TYPE_REMOVE_BUNKER]: "Забрать бункер",
        [JobType.JOB_TYPE_MSW]: "Вывоз ТБО",
        [JobType.JOB_TYPE_MSW_CLEANUP]: "Подбор",
        [JobType.JOB_TYPE_MSW_MOBILE_CLEANUP]: "Подбор с объездом на ходу",
        [JobType.JOB_TYPE_DISINFECTION]: "Дезинфекция",
        [JobType.JOB_TYPE_LIQUID_WASTE]: "Вывоз ЖБО",
        [JobType.JOB_TYPE_RECEIVE_LOAD]: "Приём груза",
        [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_REPLACE_BUNKER]: "Забрать с заменой (мультилифт)",
        [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_ADD_BUNKER]: "Установка бункера (мультилифт)",
        [JobType.JOB_TYPE_DOUBLE_CONSTRUCTION_REMOVE_BUNKER]: "Убрать бункер (мультилифт)",
        [JobType.UNRECOGNIZED]: "Не определен",
      };

  const jobType = jobAny.type ?? JobType.JOB_TYPE_UNDEFINED;
  // Handle numeric enum for JobType
  let jobTypeValue = jobType;
  if (typeof jobType === "number") {
    // Convert numeric enum to string name
    const typeName = ConnectProto.JobTypeSchema.values.find((v) => v.number === jobType)?.name;
    jobTypeValue = typeName || JobType.JOB_TYPE_UNDEFINED;
  }
  const typeText = typeMap[jobTypeValue] || typeMap[JobType.JOB_TYPE_UNDEFINED] || "Не определен";

  return {
    id: jobAny.id,
    number: `Z${jobAny.id}`,
    typeText: typeText,
    customer: serviceReceiver?.name ?? "—",
    objectName: pickupLocation?.name ?? "—",
    priorityText: priorityText,
    statusText: (statusMap as any)[jobAny.status] ?? String(jobAny.status),
    status: statusName as JobStatus,
    geozone: pickupLocation?.geo_group ?? null,
    route: routeId ? `M${routeId}` : "-",
    createdAt: toISOString(createdAt),
    finishedAt: toISOString(factEndAt) || null,
    // "Has a photo report" means driver evidence. Dispatcher-attached instructions
    // also live in file_reports and must not light this up (or match its filters)
    // for a job nobody has serviced yet.
    hasPhotoReport: Boolean(
      serviceStartedReportUrl ||
        serviceEndedReportUrl ||
        fileReports.some(
          (f: any) => f?.type !== INSTRUCTION_TYPE && f?.type !== INSTRUCTION_TYPE_NAME,
        ),
    ),
    pickup_location: pickupLocation,
    service_receiver: serviceReceiver,
    tariff: jobTariff ?? pickupLocation?.tariff ?? "-",
    driver: driverOnShift,
    plan_start_at: planStartAt ?? null,
    comment: driverComment ?? "",
    job: normalizeJobDeep(jobAny),
    rawJob: j as ConnectProto.Job,
    is_created_by_client: isCreatedByClient ?? false,
    whatsapp_author_id: whatsappAuthorId ? String(whatsappAuthorId) : undefined,
    author_first_name: authorFirstName,
    author_last_name: authorLastName,
    service_provider: serviceProvider,
    service_provider_id: serviceProviderId,
    visible_to_ai: Boolean(visibleToAi),
    author: author,
    containertype: containerType,
  };
}

export const adaptJobs = (arr: JobInput[], t?: any) => arr.map((job) => adaptJobToRow(job, t));
